import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  User, Trash2,
  Users, Calendar, FileText, X, Search,
  Unlock, Lock as LockIcon, Info, ChevronUp, ChevronDown, Lock,
  UserPlus, UserMinus, Filter, Download, MapPin
} from 'lucide-react';
import { City } from 'country-state-city';
import Button from '../components/ui/button';
import { getTicket, updateTicket } from '../api/tickets';
import { getAttachments, uploadAttachment, deleteAttachment, downloadAttachment } from '../api/attachments';
import { addComment } from '../api/comments';
import { getTicketFollowers, addFollower, removeFollower } from '../api/followers';
import { getTicketWorkNotesList, addToWorkNotesList, removeFromWorkNotesList } from '../api/workNotesList';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { getUsers } from '../api/users';
import DashboardLayout from '../components/DashboardLayout';
import TicketDetailHeader from '../components/TicketDetailHeader';
import ReferenceLookupModal from '../components/ReferenceLookupModal';
import { cleanUserName as cleanUserNameUtil } from '../lib/utils';

// --- DATA CONSTANTS ---
// Fetch all cities once.
const INDIAN_CITIES = City.getCitiesOfCountry('IN')
  .map(city => city.name)
  .sort(); 
const PREDEFINED_REGIONS = ["Mumbai", "MP and CG", "ROM", "NCR", "ROI", "Bhopal", "Indore", "Raipur", "Cantik Office"];

// Tooltip component
const Tooltip = ({ children, content, position = 'top' }) => {
  const [show, setShow] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShow(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setShow(false), 100);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  };

  return (
    <div
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {show && content && (
        <div
          className={`absolute z-50 px-2 py-1 text-xs text-white bg-slate-900 rounded shadow-lg whitespace-nowrap ${positionClasses[position]}`}
        >
          {content}
          <div className={`absolute ${position === 'top' ? 'top-full' : 'bottom-full'} left-1/2 -translate-x-1/2 border-4 border-transparent ${position === 'top' ? 'border-t-slate-900' : 'border-b-slate-900'}`} />
        </div>
      )}
    </div>
  );
};

// Reference field with icon buttons
const ReferenceField = ({
  value,
  onChange,
  placeholder = 'Search...',
  items = [],
  displayKey = 'name',
  disabled = false,
  showLock = false,
  showInfo = true,
  showSearch = true,
  showCalendar = false,
  infoTooltip = '',
  fieldName = '',
  showDetails = false, // Show description, email, member count
  searchFields = [], // Additional fields to search (e.g., ['description', 'email'])
  onSearchClick = null, // Callback to open modal instead of dropdown
}) => {
  const selected = items.find(i => i.id?.toString() === value) || null;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isLocked, setIsLocked] = useState(false);

  const filtered = useMemo(() => {
    if (!query) return items.slice(0, 6);
    const queryLower = query.toLowerCase();
    return items
      .filter(i => {
        // Search in displayKey (usually 'name')
        const mainMatch = (i[displayKey] || '').toLowerCase().includes(queryLower);
        // Search in additional fields if provided
        const additionalMatch = searchFields.some(field => 
          (i[field] || '').toLowerCase().includes(queryLower)
        );
        return mainMatch || additionalMatch;
      })
      .slice(0, 6);
  }, [items, query, displayKey, searchFields]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (open && !event.target.closest('.reference-dropdown') && !event.target.closest('.reference-field')) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const displayValue = selected ? selected[displayKey] : (value ? items.find(i => i.id?.toString() === value)?.[displayKey] || value : '');

  const handleLockToggle = () => {
    setIsLocked(!isLocked);
    toast.info(isLocked ? `${fieldName || 'Field'} unlocked` : `${fieldName || 'Field'} locked`);
  };

  return (
    <div className="relative reference-field">
      <input
        type="text"
        value={displayValue}
        readOnly
        onClick={() => !disabled && !isLocked && setOpen(true)}
        placeholder={placeholder}
        disabled={disabled || isLocked}
        className={`w-full border border-gray-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 pr-20 px-4 py-2.5 text-sm transition-all duration-200 ${
          isLocked ? 'bg-gray-100/80 dark:bg-slate-800/60 cursor-not-allowed opacity-75' : ''
        } ${!disabled && !isLocked ? 'cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500' : ''}`}
      />
      <div className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
        {showSearch && (
          <Tooltip content="Search and select">
            <button
              type="button"
              className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                // Allow modal to open even when field is disabled (for view-only)
                if (onSearchClick) {
                  onSearchClick();
                } else if (!disabled && !isLocked) {
                  setOpen(true);
                }
              }}
              disabled={!onSearchClick && (disabled || isLocked)}
            >
              <Search className="w-4 h-4" />
            </button>
          </Tooltip>
        )}
        {showLock && (
          <Tooltip content={isLocked ? 'Unlock field' : 'Lock field'}>
            <button
              type="button"
              className={`p-2 rounded-xl border border-gray-200 dark:border-slate-700 transition-all duration-200 ${
                isLocked
                  ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700'
                  : 'bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 text-gray-500 dark:text-gray-400'
              }`}
              onClick={handleLockToggle}
            >
              {isLocked ? <LockIcon className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
            </button>
          </Tooltip>
        )}
        {showInfo && (
          <Tooltip content={infoTooltip || `Information about ${fieldName || 'this field'}`}>
            <button
              type="button"
              className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200"
            >
              <Info className="w-4 h-4" />
            </button>
          </Tooltip>
        )}
        {showCalendar && (
          <Tooltip content="Select date">
            <button
              type="button"
              className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200"
            >
              <Calendar className="w-4 h-4" />
            </button>
          </Tooltip>
        )}
      </div>
      {open && !disabled && !isLocked && (
        <div className="reference-dropdown absolute left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-lg z-20 animate-in fade-in slide-in-from-top-1">
          <div className="p-2 border-b border-gray-200 dark:border-slate-700">
            <input
              className="w-full border border-gray-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all duration-200"
              placeholder="Type to search…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-sm text-slate-500 dark:text-slate-400">No results</div>
            ) : (
              filtered.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-slate-700 text-sm transition-all duration-200 rounded mx-1"
                  onClick={() => {
                    onChange(item.id.toString());
                    setOpen(false);
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-slate-900 dark:text-slate-100 block truncate">
                        {item[displayKey]}
                      </span>
                      {showDetails && (
                        <div className="mt-1 space-y-0.5">
                          {item.description && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                              {item.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
                            {item.email && (
                              <span className="truncate">{item.email}</span>
                            )}
                            {item.member_count !== undefined && item.member_count !== null && (
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {item.member_count} {item.member_count === 1 ? 'member' : 'members'}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Use the shared utility function for cleaning user names
const cleanUserName = cleanUserNameUtil;

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const normalizedRole = String(user?.role || '').toLowerCase();
  const isStaffPortal =
    normalizedRole === 'admin' || normalizedRole === 'employee' || normalizedRole === 'super_admin';
  const staffTicketsHome = normalizedRole === 'super_admin' ? '/super-admin/tickets' : '/admin/tickets';
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [comment, setComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [assignmentGroups, setAssignmentGroups] = useState([]);
  const [notesExpanded, setNotesExpanded] = useState(true);
  const [activityExpanded, setActivityExpanded] = useState(true);
  const [activityFilter, setActivityFilter] = useState('all'); // 'all' | 'comments' | 'history'
  const [activityFilterOpen, setActivityFilterOpen] = useState(false);
  const activityFilterRef = useRef(null);
  const [watchList, setWatchList] = useState([]);
  const [watchListLoading, setWatchListLoading] = useState(false);
  const [showAddWatchListModal, setShowAddWatchListModal] = useState(false);
  const [watchListModalSearch, setWatchListModalSearch] = useState('');
  const [tenantAdminsWatchUsers, setTenantAdminsWatchUsers] = useState([]);
  const [watchListLocked, setWatchListLocked] = useState(false);
  const [workNotesList, setWorkNotesList] = useState([]);
  const [workNotesListLoading, setWorkNotesListLoading] = useState(false);
  const [showAddWorkNotesListModal, setShowAddWorkNotesListModal] = useState(false);
  const [workNotesLocked, setWorkNotesLocked] = useState(false);
  const [additionalComments, setAdditionalComments] = useState('');
  const [workNotes, setWorkNotes] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [lookupModal, setLookupModal] = useState({ isOpen: false, field: null, data: [], title: '', columns: [], displayKey: 'name' });
  const [showCustomSubcategory, setShowCustomSubcategory] = useState(false);
  const [customSubcategoryName, setCustomSubcategoryName] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const locationWrapperRef = useRef(null);
  const [formData, setFormData] = useState({
    status: '',
    priority: '',
    assigned_to: '',
    resolution: '',
    subject: '',
    description: '',
    category_id: '',
    subcategory_id: '',
    assignment_group_id: '',
    location: '',
    contact_type: 'phone',
    impact: '3',
    urgency: '3',
  });

  const fetchWatchList = async () => {
    if (!id) return;
    try {
      setWatchListLoading(true);
      const res = await getTicketFollowers(id);
      if (res.success && Array.isArray(res.data)) {
        setWatchList(res.data.map((u) => ({ ...u, name: cleanUserName(u.name || '') })));
      } else {
        setWatchList([]);
      }
    } catch (err) {
      console.error('Failed to fetch watch list:', err);
      setWatchList([]);
    } finally {
      setWatchListLoading(false);
    }
  };

  const fetchWorkNotesListData = async () => {
    if (!id || !isStaffPortal) return;
    try {
      setWorkNotesListLoading(true);
      const res = await getTicketWorkNotesList(id);
      if (res.success && Array.isArray(res.data)) {
        setWorkNotesList(res.data.map((u) => ({ ...u, name: cleanUserName(u.name || '') })));
      } else {
        setWorkNotesList([]);
      }
    } catch (err) {
      console.error('Failed to fetch work notes list:', err);
      setWorkNotesList([]);
    } finally {
      setWorkNotesListLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
    fetchUsers();
    fetchCategories();
    fetchAssignmentGroups();
    fetchWatchList();
    fetchWorkNotesListData();
    
    const cleanDomZeros = () => {
      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );
      
      let node;
      // eslint-disable-next-line no-cond-assign
      while (node = walker.nextNode()) {
        if (node.textContent && node.textContent.match(/Customer0|adminuser0|User0/)) {
          const original = node.textContent;
          const cleaned = original.replace(/(\w+)0+/g, '$1').replace(/0+$/g, '');
          if (original !== cleaned) {
            node.textContent = cleaned;
          }
        }
      }
    };
    
    setTimeout(cleanDomZeros, 100);
    setTimeout(cleanDomZeros, 500);
    setTimeout(cleanDomZeros, 1000);
    
    if (id && user?.id) {
      const recentKey = `recent_tickets_${user.id}`;
      const recentTickets = JSON.parse(localStorage.getItem(recentKey) || '[]');
      const existingIndex = recentTickets.findIndex(t => t.ticketId === parseInt(id));
      
      if (existingIndex >= 0) {
        recentTickets.splice(existingIndex, 1);
      }
      
      recentTickets.unshift({
        ticketId: parseInt(id),
        viewedAt: new Date().toISOString()
      });
      
      const trimmed = recentTickets.slice(0, 50);
      localStorage.setItem(recentKey, JSON.stringify(trimmed));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  useEffect(() => {
    const cleanZeros = () => {
      const allElements = document.querySelectorAll('span, div, p, td, li');
      allElements.forEach(el => {
        if (el.textContent && el.textContent.match(/\w+0+$/)) {
          const original = el.textContent;
          const cleaned = original.replace(/(\w+)0+(\s|$)/g, '$1$2').replace(/0+$/g, '');
          if (original !== cleaned && cleaned.length > 0) {
            if (el.childNodes.length === 1 && el.childNodes[0].nodeType === Node.TEXT_NODE) {
              el.textContent = cleaned;
            } else {
              const walker = document.createTreeWalker(
                el,
                NodeFilter.SHOW_TEXT,
                null,
                false
              );
              let textNode;
              // eslint-disable-next-line no-cond-assign
              while (textNode = walker.nextNode()) {
                if (textNode.textContent.match(/\w+0+$/)) {
                  const orig = textNode.textContent;
                  const clean = orig.replace(/(\w+)0+$/g, '$1');
                  if (orig !== clean) {
                    textNode.textContent = clean;
                  }
                }
              }
            }
          }
        }
      });
    };

    cleanZeros();
    const interval = setInterval(cleanZeros, 500);
    return () => clearInterval(interval);
  }, [ticket]);

  const fetchTicket = async () => {
    try {
      const response = await getTicket(id);
      if (response.success) {
        const ticketData = response.data;
        
        if (ticketData.customer_name) {
          ticketData.customer_name = cleanUserName(ticketData.customer_name);
        }
        if (ticketData.assigned_to_name) {
          ticketData.assigned_to_name = cleanUserName(ticketData.assigned_to_name);
        }
        if (ticketData.opened_by_name) {
          ticketData.opened_by_name = cleanUserName(ticketData.opened_by_name);
        }
        
        if (ticketData.comments && Array.isArray(ticketData.comments)) {
          ticketData.comments = ticketData.comments.map(comment => {
            if (comment.user_name) {
              let cleaned = cleanUserName(String(comment.user_name).trim());
              while (cleaned.endsWith('0')) {
                cleaned = cleaned.slice(0, -1).trim();
              }
              cleaned = cleanUserName(cleaned);
              comment.user_name = cleaned;
            }
            return comment;
          });
        }
        
        if (ticketData.history && Array.isArray(ticketData.history)) {
          // Sort history to ensure entries with new_value (like ticket numbers) come first for deduplication
          const sortedHistory = [...ticketData.history].sort((a, b) => {
            if (a.action === 'created' && b.action === 'created') {
              if (a.new_value && !b.new_value) return -1;
              if (!a.new_value && b.new_value) return 1;
            }
            return 0;
          });

          // Deduplicate "created" actions to avoid showing "Ticket created" twice
          const seenCreated = new Set();
          ticketData.history = sortedHistory.filter(item => {
            const isCreatedAction = item.action === 'created' || item.action === 'ticket.created' || item.action === 'ticket_created';
            if (isCreatedAction) {
              if (seenCreated.has('created')) return false;
              seenCreated.add('created');
              // Normalize action to 'created' for consistent formatting
              item.action = 'created';
            }
            return true;
          });

          // Sort back to chronological order (assuming they have created_at)
          ticketData.history.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

          ticketData.history = ticketData.history.map(item => {
            if (item.user_name) {
              let cleaned = cleanUserName(String(item.user_name).trim());
              while (cleaned.endsWith('0')) {
                cleaned = cleaned.slice(0, -1).trim();
              }
              cleaned = cleanUserName(cleaned);
              item.user_name = cleaned;
            }
            return item;
          });
        }
        
        if (!ticketData.ticket_number && ticketData.ticketNumber) {
          ticketData.ticket_number = ticketData.ticketNumber;
        }
        if (ticketData.ticketNumber && !ticketData.ticket_number) {
          ticketData.ticket_number = ticketData.ticketNumber;
        }
        
        setTicket(ticketData);

        if (ticketData.tenant_id && isStaffPortal) {
          try {
            const assignRes = await getUsers('employee,admin', {
              assignmentTenantId: ticketData.tenant_id,
            });
            if (assignRes.success) {
              setEmployees(assignRes.data || []);
            }
          } catch (assignErr) {
            console.error('Failed to load assignable agents:', assignErr);
          }
        }

        // FIX: Correctly identify if subcategory_id is "empty" (null, undefined, 0, "0", or "")
        const subId = ticketData.subcategory_id;
        const isSubIdEmpty = !subId || subId === 0 || subId === '0' || subId === '';
        
        // Check if ticket has a custom subcategory
        const hasCustomSubcategory = ticketData.subcategory_name && isSubIdEmpty;
        
        if (hasCustomSubcategory) {
          setShowCustomSubcategory(true);
          setCustomSubcategoryName(ticketData.subcategory_name);
        } else {
          setShowCustomSubcategory(false);
          setCustomSubcategoryName('');
        }
        
        setFormData({
          // Normalize DB statuses like OPEN / IN_PROGRESS / RESOLVED / CLOSED to our internal values
          status: (() => {
            const raw = String(ticketData.status || '').toUpperCase();
            if (raw === 'IN_PROGRESS' || raw === 'ASSIGNED') return 'in_progress';
            if (raw === 'OPEN') return 'open';
            if (raw === 'RESOLVED') return 'resolved';
            if (raw === 'CLOSED') return 'closed';
            return raw.toLowerCase();
          })(),
          priority: ticketData.priority || '',
          assigned_to: ticketData.assigned_to || '',
          resolution: ticketData.resolution || '',
          subject: ticketData.subject || '',
          description: ticketData.description || '',
          category_id: ticketData.category_id || '',
          // If custom, force "custom" so the dropdown selects the correct option
          subcategory_id: hasCustomSubcategory ? 'custom' : (ticketData.subcategory_id || ''),
          assignment_group_id: ticketData.assignment_group_id || '',
          location: ticketData.location || '',
          contact_type: ticketData.contact_type || 'phone',
          impact: ticketData.impact || '3',
          urgency: ticketData.urgency || '3',
        });
        if (ticketData.category_id) {
          fetchSubcategories(ticketData.category_id);
        }
      }
    } catch (error) {
      toast.error('Failed to load ticket');
      if (isStaffPortal) {
        navigate(staffTicketsHome);
      } else {
        navigate('/customer/tickets');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && ticket) {
      fetchAttachments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, ticket]);

  const fetchAttachments = async () => {
    if (!id) return;
    setAttachmentsLoading(true);
    try {
      const response = await getAttachments(id);
      if (response.success) {
        setAttachments(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load attachments:', error);
      toast.error('Failed to load attachments');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      if (res.success) {
        setUsers(res.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories');
      if (res.data?.success) {
        setCategories(res.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const res = await api.get(`/subcategories?category_id=${categoryId}`);
      if (res.data?.success) {
        setSubcategories(res.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch subcategories:', err);
    }
  };

  const filteredLocationOptions = useMemo(() => {
    const query = (formData.location || '').toLowerCase().trim();

    if (!query) {
      return { 
        regions: PREDEFINED_REGIONS, 
        cities: INDIAN_CITIES.slice(0, 20) 
      };
    }
    
    const matchedRegions = PREDEFINED_REGIONS.filter(r => 
      r.toLowerCase().startsWith(query)
    );
    
    let matchedCities = INDIAN_CITIES.filter(c => 
      c.toLowerCase().startsWith(query)
    );

    matchedCities.sort((a, b) => a.length - b.length);

    return { 
      regions: matchedRegions, 
      cities: matchedCities.slice(0, 50) 
    };
  }, [formData.location]);

  const effectiveWatchTenantId = useMemo(() => {
    if (ticket?.tenant_id != null && ticket.tenant_id !== '') return ticket.tenant_id;
    return user?.tenant_id ?? null;
  }, [ticket?.tenant_id, user?.tenant_id]);

  const normalizedRoleForWatch = useMemo(() => String(user?.role || '').toLowerCase(), [user?.role]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getUsers('admin');
        if (!res.success || !Array.isArray(res.data)) {
          if (!cancelled) setTenantAdminsWatchUsers([]);
          return;
        }
        let rows = res.data;
        if (normalizedRoleForWatch === 'super_admin') {
          const tid = effectiveWatchTenantId;
          if (tid == null || tid === '') rows = [];
          else rows = rows.filter((u) => Number(u.tenant_id) === Number(tid));
        }
        if (!cancelled) setTenantAdminsWatchUsers(rows);
      } catch {
        if (!cancelled) setTenantAdminsWatchUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [normalizedRoleForWatch, effectiveWatchTenantId]);

  const watchListPickerUsers = useMemo(() => {
    const map = new Map();
    (users || []).forEach((u) => {
      if (u?.id != null) map.set(Number(u.id), u);
    });
    (tenantAdminsWatchUsers || []).forEach((u) => {
      if (u?.id != null) map.set(Number(u.id), u);
    });
    return Array.from(map.values());
  }, [users, tenantAdminsWatchUsers]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationWrapperRef.current && !locationWrapperRef.current.contains(event.target)) {
        setShowLocationDropdown(false);
      }
      if (activityFilterRef.current && !activityFilterRef.current.contains(event.target)) {
        setActivityFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubcategoryChange = (value) => {
    if (value === 'custom') {
      setShowCustomSubcategory(true);
      setFormData(prev => ({ ...prev, subcategory_id: 'custom' }));
    } else {
      setShowCustomSubcategory(false);
      setCustomSubcategoryName('');
      setFormData(prev => ({ ...prev, subcategory_id: value }));
    }
  };

  const handleCustomSubcategoryChange = (value) => {
    setCustomSubcategoryName(value);
    setFormData(prev => ({ ...prev, subcategory_id: 'custom' }));
  };

  const fetchAssignmentGroups = async () => {
    try {
      const res = await api.get('/assignment-groups');
      if (res.data?.success) {
        setAssignmentGroups(res.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch assignment groups:', err);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setSubmitting(true);
    try {
      const response = await addComment(id, {
        comment: comment.trim(),
        is_internal: isInternal,
      });

      if (response.success) {
        toast.success('Comment added successfully');
        setComment('');
        setIsInternal(false);
        fetchTicket(); 
      }
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateTicket = async () => {
    setSubmitting(true);
    try {
      // FIX: Ensure ID is a string before trimming to prevent crash on numeric IDs
      const subIdStr = String(formData.subcategory_id || '');
      
      // Determine values based on showCustomSubcategory flag (UI State) for maximum reliability
      const effectiveSubcategoryId = showCustomSubcategory ? null : (subIdStr.trim() && !isNaN(parseInt(subIdStr, 10)) ? parseInt(subIdStr, 10) : null);
      const effectiveSubcategoryName = showCustomSubcategory && String(customSubcategoryName || '').trim() ? String(customSubcategoryName).trim() : null;

      const updatePayload = isCustomer && canCustomerEdit
        ? {
            subject: formData.subject,
            description: formData.description,
            location: formData.location || null,
            contact_type: formData.contact_type,
          }
        : {
            status: formData.status,
            priority: formData.priority,
            assigned_to: formData.assigned_to || null,
            resolution: formData.resolution || null,
            subject: formData.subject,
            description: formData.description,
            category_id: formData.category_id || null,
            subcategory_id: effectiveSubcategoryId,
            subcategory_name: effectiveSubcategoryName,
            assignment_group_id: formData.assignment_group_id || null,
            location: formData.location || null,
            contact_type: formData.contact_type,
            impact: formData.impact,
            urgency: formData.urgency,
          };

      const response = await updateTicket(id, updatePayload);
      if (response.success) {
        toast.success('Ticket updated successfully');
        setIsEditing(false);
        fetchTicket();
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to update ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddAdditionalComment = async () => {
    if (!additionalComments.trim()) return;
    setSubmitting(true);
    try {
      const response = await addComment(id, {
        comment: additionalComments.trim(),
        is_internal: false,
      });
      if (response.success) {
        toast.success('Additional comment added');
        setAdditionalComments('');
        fetchTicket();
      }
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddWorkNote = async () => {
    if (!workNotes.trim()) return;
    setSubmitting(true);
    try {
      const response = await addComment(id, {
        comment: workNotes.trim(),
        is_internal: true,
      });
      if (response.success) {
        toast.success('Work note added');
        setWorkNotes('');
        fetchTicket();
      }
    } catch (error) {
      toast.error('Failed to add work note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAttachmentFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setUploadFiles(files);
  };

  const handleUploadAttachments = async (e) => {
    e.preventDefault();
    if (!uploadFiles.length) return;
    setSubmitting(true);
    try {
      for (const file of uploadFiles) {
        try {
          const res = await uploadAttachment(id, file);
          if (!res.success) {
            toast.error(`Failed to upload ${file.name}`);
          }
        } catch (error) {
          console.error('Attachment upload error:', error);
          toast.error(`Failed to upload ${file.name}`);
        }
      }
      setUploadFiles([]);
      if (e.target && e.target.closest('form')) {
        const input = e.target.closest('form').querySelector('input[type="file"]');
        if (input) input.value = '';
      }
      await fetchAttachments();
      toast.success('Attachments uploaded');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    setSubmitting(true);
    try {
      const res = await deleteAttachment(attachmentId);
      if (res.success) {
        toast.success('Attachment deleted');
        await fetchAttachments();
      } else {
        toast.error('Failed to delete attachment');
      }
    } catch (error) {
      console.error('Delete attachment error:', error);
      toast.error('Failed to delete attachment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await downloadAttachment(attachment.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.file_name || 'attachment';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download attachment error:', error);
      toast.error('Failed to download attachment');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const getStatusColor = (status) => {
    const s = String(status || '').toLowerCase();
    switch (s) {
      case 'open':
      case 'new':
        return 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20';
      case 'in_progress':
        return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
      case 'resolved':
        return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20';
      case 'closed':
        return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20';
      case 'pending':
        return 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20';
      case 'cancelled':
        return 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20';
      default:
        return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20';
    }
  };

  // eslint-disable-next-line no-unused-vars
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20';
      case 'high':
        return 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20';
      case 'medium':
        return 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20';
      case 'low':
        return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
      default:
        return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20';
    }
  };

  const handleQuickAction = async (action) => {
    setSubmitting(true);
    try {
      let updatePayload = {};
      
      switch (action) {
        case 'resolve':
          updatePayload = { status: 'resolved' };
          break;
        case 'close':
          updatePayload = { status: 'closed' };
          break;
        case 'reopen':
          updatePayload = { status: 'open' };
          break;
        case 'in_progress':
          updatePayload = { status: 'in_progress' };
          break;
        default:
          return;
      }

      const response = await updateTicket(id, updatePayload);
      if (response.success) {
        toast.success(`Ticket ${action.replace('_', ' ')} successfully`);
        fetchTicket();
      }
    } catch (error) {
      toast.error(`Failed to ${action} ticket`);
    } finally {
      setSubmitting(false);
    }
  };

  const canUpdate = isStaffPortal;
  const isCustomer = normalizedRole === 'customer';
  const canCustomerEdit = isCustomer && ticket && ticket.customer_id === user?.id;
  const canManageWatchList = !watchListLocked && (canUpdate || (ticket && ticket.customer_id === user?.id));

  const handleAddToWatchList = async (userId) => {
    if (!id || !userId) return;
    try {
      const res = await addFollower(id, userId);
      if (res.success) {
        toast.success('User added to Watch List');
        setShowAddWatchListModal(false);
        setWatchListModalSearch('');
        fetchWatchList();
      } else {
        toast.error(res.message || 'Failed to add user');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add to Watch List');
    }
  };

  const handleRemoveFromWatchList = async (userId) => {
    if (!id) return;
    try {
      await removeFollower(id, userId);
      toast.success('User removed from Watch List');
      fetchWatchList();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove from Watch List');
    }
  };

  const canManageWorkNotesList = !workNotesLocked && canUpdate;

  const handleAddToWorkNotesList = async (userId) => {
    if (!id || !userId) return;
    try {
      const res = await addToWorkNotesList(id, userId);
      if (res.success) {
        toast.success('User added to Work notes list');
        setShowAddWorkNotesListModal(false);
        fetchWorkNotesListData();
      } else {
        toast.error(res.message || 'Failed to add user');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add to Work notes list');
    }
  };

  const handleRemoveFromWorkNotesList = async (userId) => {
    if (!id) return;
    try {
      await removeFromWorkNotesList(id, userId);
      toast.success('User removed from Work notes list');
      fetchWorkNotesListData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove from Work notes list');
    }
  };

  const openLookupModal = (field, data, title, columns = [], displayKey = 'name') => {
    setLookupModal({
      isOpen: true,
      field,
      data,
      title,
      columns,
      displayKey,
    });
  };

  const handleLookupSelect = (item) => {
    if (isCustomer) {
      setLookupModal({ ...lookupModal, isOpen: false });
      return;
    }
    
    if (lookupModal.field === 'assignment_group') {
      if (canUpdate && isEditing) {
        setFormData({ ...formData, assignment_group_id: item.id.toString() });
      }
    } else if (lookupModal.field === 'caller') {
      if (canUpdate && isEditing) {
        setFormData({ ...formData, customer_id: item.id.toString() });
      }
    } else if (lookupModal.field === 'assigned_to') {
      if (canUpdate && isEditing) {
        setFormData({ ...formData, assigned_to: item.id.toString() });
      }
    }
    setLookupModal({ ...lookupModal, isOpen: false });
  };

  const closeLookupModal = () => {
    setLookupModal({ ...lookupModal, isOpen: false });
  };

  const getPriorityLabel = (p) => {
    const map = {
      '1': 'Critical',
      '2': 'High',
      '3': 'Medium',
      '4': 'Low',
      '5': 'Planning',
      'urgent': 'Urgent',
      'high': 'High',
      'medium': 'Medium',
      'low': 'Low'
    };
    return map[p] || p;
  };

  const openedDisplay = ticket ? new Date(ticket.created_at).toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).replace(',', '') : '';

  if (loading) {
    return (
      <DashboardLayout userRole={user?.role || 'customer'}>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-mid mx-auto mb-4"></div>
            <p className="text-gray-500">Loading ticket...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!ticket) {
    return null;
  }

  const formatHistoryItem = (item) => {
    if (item.action === 'comment_added') {
      try {
        if (item.new_value && item.new_value.startsWith('{')) {
          const parsed = JSON.parse(item.new_value);
          if (parsed.type === 'customer_comment' && parsed.text) {
            const text = parsed.text.length > 80 ? parsed.text.substring(0, 80) + '...' : parsed.text;
            return `Added comment: "${text}"`;
          }
        }
        return 'Comment added';
      } catch (error) {
        return 'Comment added';
      }
    }

    if (item.action === 'work_note_added') {
      try {
        if (item.new_value && item.new_value.startsWith('{')) {
          const parsed = JSON.parse(item.new_value);
          if (parsed.type === 'work_note' && parsed.text) {
            const text = parsed.text.length > 80 ? parsed.text.substring(0, 80) + '...' : parsed.text;
            return `Added work note: "${text}"`;
          }
        }
        return 'Work note added';
      } catch (error) {
        return 'Work note added';
      }
    }

    if (item.action === 'created') {
      if (item.new_value && !item.new_value.startsWith('{')) {
        // If the new_value already contains "Ticket created", don't prefix it again
        if (String(item.new_value).toLowerCase().includes('ticket created')) {
          return item.new_value;
        }
        return `Ticket created: ${item.new_value}`;
      }
      return 'Ticket created';
    }

    try {
      let newValue = {};
      let oldValue = {};

      if (item.new_value) {
        if (item.new_value.startsWith('{')) {
          newValue = JSON.parse(item.new_value);
        } else {
          return item.new_value;
        }
      }

      if (item.old_value && item.old_value.startsWith('{')) {
        oldValue = JSON.parse(item.old_value);
      }

      const changes = [];

      if (newValue.status && newValue.status !== oldValue.status) {
        const oldStatus = oldValue.status ? oldValue.status.replace('_', ' ').toUpperCase() : 'N/A';
        const newStatus = newValue.status.replace('_', ' ').toUpperCase();
        changes.push(`Status: ${oldStatus} → ${newStatus}`);
      }

      if (newValue.priority && newValue.priority !== oldValue.priority) {
        const oldPriority = oldValue.priority ? oldValue.priority.toUpperCase() : 'N/A';
        const newPriority = newValue.priority.toUpperCase();
        changes.push(`Priority: ${oldPriority} → ${newPriority}`);
      }

      if (newValue.assigned_to !== undefined && newValue.assigned_to !== oldValue.assigned_to) {
        if (newValue.assigned_to) {
          const assignedUser = employees.find(e => e.id.toString() === newValue.assigned_to.toString());
          const userName = assignedUser ? cleanUserName(assignedUser.name) : 'User #' + newValue.assigned_to;
          changes.push(`Assigned to: ${userName}`);
        } else {
          changes.push('Unassigned');
        }
      }

      if (newValue.resolution) {
        if (!oldValue.resolution) {
          const resolutionText = newValue.resolution.length > 60 
            ? newValue.resolution.substring(0, 60) + '...' 
            : newValue.resolution;
          changes.push(`Resolution: ${resolutionText}`);
        } else if (newValue.resolution !== oldValue.resolution) {
          const resolutionText = newValue.resolution.length > 60 
            ? newValue.resolution.substring(0, 60) + '...' 
            : newValue.resolution;
          changes.push(`Resolution updated: ${resolutionText}`);
        }
      }

      if (newValue.subject && newValue.subject !== oldValue.subject) {
        changes.push(`Subject changed`);
      }

      if (newValue.description && newValue.description !== oldValue.description) {
        changes.push(`Description updated`);
      }

      if (changes.length > 0) {
        return changes.join(' • ');
      }

      return item.action.replace('_', ' ').charAt(0).toUpperCase() + item.action.replace('_', ' ').slice(1);
    } catch (error) {
      return item.action.replace('_', ' ').charAt(0).toUpperCase() + item.action.replace('_', ' ').slice(1);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      if (isStaffPortal) {
        navigate(staffTicketsHome);
      } else {
        navigate('/customer/tickets');
      }
    }
  };

  return (
    <>
    <DashboardLayout userRole={user?.role || 'customer'}>
      <div className="flex flex-col overflow-hidden h-full min-h-0">
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-200/70 dark:border-slate-700/70 px-4 py-3 shadow-sm">
          <TicketDetailHeader
            ticketNumber={ticket.ticket_number}
            status={ticket.status}
            subject={ticket.subject}
            customerName={cleanUserName(ticket.customer_name || '')}
            isEditing={isEditing}
            canUpdate={canUpdate}
            canResolve={canUpdate && ticket.status !== 'resolved' && ticket.status !== 'closed'}
            canClose={canUpdate && ticket.status !== 'closed'}
            submitting={submitting}
            onBack={handleBack}
            onEdit={() => setIsEditing(true)}
            onCancelEdit={() => {
              setIsEditing(false);
              setShowCustomSubcategory(false);
              setCustomSubcategoryName('');
              fetchTicket();
            }}
            onSave={handleUpdateTicket}
            onResolve={() => handleQuickAction('resolve')}
            onClose={() => handleQuickAction('close')}
          />
        </div>

        <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50 via-gray-50/70 to-indigo-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950/30 p-4 md:p-6">
          <div className="w-full max-w-none relative bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl md:rounded-2xl border border-gray-200/60 dark:border-slate-700/60 overflow-hidden shadow-lg shadow-indigo-500/5">
            <div className={`p-4 md:p-5 border-b border-dashed border-gray-300/60 dark:border-slate-700/60 ${isEditing ? 'bg-gradient-to-br from-indigo-50/30 via-white to-blue-50/20 dark:from-indigo-950/20 dark:via-slate-800 dark:to-blue-950/10 relative overflow-hidden' : ''}`}>
              <form className={`grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 md:gap-y-3 ${isEditing ? 'relative z-10' : ''}`}>
                <div className="space-y-4 md:space-y-3">
                  <div className="flex flex-col gap-1.5 md:grid md:grid-cols-4 md:items-center">
                    <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 md:col-span-1" htmlFor="number">
                      Number
                    </label>
                    <div className="md:col-span-3">
                      <input
                        className="w-full bg-gray-100/80 dark:bg-slate-800/60 border border-dashed border-gray-300 dark:border-slate-600 rounded-xl text-gray-500 dark:text-gray-400 px-4 py-2.5 md:py-2.5 text-sm font-mono font-medium cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500/60 min-h-[44px] md:min-h-0"
                        id="number"
                        readOnly
                        type="text"
                        value={ticket.ticket_number ? `#${ticket.ticket_number}` : '#TKT...'}
                      />
          </div>
        </div>

                  <div className="flex flex-col gap-1.5 md:grid md:grid-cols-4 md:items-center">
                    <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 md:col-span-1" htmlFor="caller">
                      Caller
                    </label>
                    <div className="md:col-span-3">
                      <ReferenceField
                        value={ticket.customer_id?.toString() || ''}
                        onChange={(val) => setFormData({ ...formData, customer_id: val })}
                        items={users}
                        placeholder="Search..."
                        disabled={!canUpdate || !isEditing}
                        showLock={canUpdate && isEditing}
                        showInfo={false}
                        showSearch={true}
                        infoTooltip="Select the person who reported this incident"
                        fieldName="Caller"
                        onSearchClick={() => openLookupModal('caller', users, 'Select Caller', [
                          { key: 'name', label: 'Name', width: '25%' },
                          { key: 'email', label: 'Email', width: '30%' },
                          { key: 'mobile_phone', label: 'Phone', width: '20%' },
                          { key: 'job_title', label: 'Title', width: '15%' },
                          { key: 'department', label: 'Department', width: '10%' }
                        ], 'name')}
                      />
                      {isCustomer && !isEditing && (
                        <input
                          type="text"
                          className="w-full border border-dashed border-gray-300 dark:border-slate-600 rounded-xl bg-gray-100/80 dark:bg-slate-800/60 text-gray-500 dark:text-gray-400 px-4 py-2.5 text-sm cursor-not-allowed mt-2 transition-all duration-200"
                          value={cleanUserName(ticket.customer_name || '')}
                          readOnly
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5 md:grid md:grid-cols-4 md:items-center md:gap-3" ref={locationWrapperRef}>
                    <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 md:col-span-1" htmlFor="location">
                      Location
                    </label>
                    <div className="md:col-span-3 relative">
                      <div className="relative">
                        <input
                          type="text"
                          id="location"
                          value={formData.location}
                          onChange={(e) => {
                            setFormData({ ...formData, location: e.target.value });
                            setShowLocationDropdown(true);
                          }}
                          onFocus={() => {
                            if (canUpdate && isEditing) {
                              setShowLocationDropdown(true);
                            }
                          }}
                          placeholder="Search city..."
                          className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 pr-10 disabled:bg-gray-100/80 dark:disabled:bg-slate-800/60 disabled:cursor-not-allowed min-h-[44px] md:min-h-0"
                          autoComplete="off"
                          disabled={!isEditing || (isCustomer && !canCustomerEdit) || !canUpdate}
                        />
                        <div className="absolute right-3 top-2.5 text-gray-400 dark:text-gray-500 pointer-events-none">
                          <MapPin className="w-4 h-4" />
                        </div>
                  </div>
                  
                      {showLocationDropdown && canUpdate && isEditing && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-150">
                           
                           {filteredLocationOptions.regions.length > 0 && (
                             <>
                               <div className="px-4 py-2 text-xs font-bold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-slate-700/50 uppercase tracking-wider sticky top-0 backdrop-blur-sm">
                                 Regions
                        </div>
                               {filteredLocationOptions.regions.map(region => (
                                 <button
                                   key={region}
                                   type="button"
                                   className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-indigo-50 dark:hover:bg-slate-700 transition-colors border-b border-gray-100 dark:border-slate-700/50 last:border-0"
                                   onClick={() => {
                                     setFormData({ ...formData, location: region });
                                     setShowLocationDropdown(false);
                                   }}
                                 >
                                   {region}
                                 </button>
                               ))}
                             </>
                           )}

                           {filteredLocationOptions.cities.length > 0 && (
                             <>
                               <div className="px-4 py-2 text-xs font-bold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-slate-700/50 uppercase tracking-wider sticky top-0 backdrop-blur-sm border-t border-gray-200 dark:border-slate-700">
                                 Cities
                      </div>
                               
                               {filteredLocationOptions.cities.map(city => (
                                 <button
                                   key={city}
                                   type="button"
                                   className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-indigo-50 dark:hover:bg-slate-700 transition-colors border-b border-gray-100 dark:border-slate-700/50 last:border-0"
                                   onClick={() => {
                                     setFormData({ ...formData, location: city });
                                     setShowLocationDropdown(false);
                                   }}
                                 >
                                   {city}
                                 </button>
                               ))}
                             </>
                           )}

                           {filteredLocationOptions.regions.length === 0 && filteredLocationOptions.cities.length === 0 && (
                             <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 italic text-center">
                               No matches found. Using custom location: <span className="font-semibold text-indigo-500">"{formData.location}"</span>
                    </div>
                  )}
                        </div>
                      )}
                    </div>
                </div>

                  <div className="flex flex-col gap-1.5 md:grid md:grid-cols-4 md:items-center">
                    <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 md:col-span-1" htmlFor="category">
                      Category
                    </label>
                    <div className="md:col-span-3">
                        <select
                        className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 disabled:bg-gray-100/80 dark:disabled:bg-slate-800/60 disabled:cursor-not-allowed"
                        id="category"
                        value={formData.category_id}
                        onChange={e => {
                          setFormData({ ...formData, category_id: e.target.value, subcategory_id: '' });
                          setShowCustomSubcategory(false);
                          setCustomSubcategoryName('');
                          if (e.target.value) fetchSubcategories(parseInt(e.target.value));
                        }}
                        disabled={!canUpdate || !isEditing}
                        title={isCustomer ? "Category cannot be changed after ticket creation" : ""}
                      >
                        <option value="">-- Select Category --</option>
                        {categories.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                        </select>
                      </div>
                      </div>

                  <div className="flex flex-col gap-1.5 md:grid md:grid-cols-4 md:items-center">
                    <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 md:col-span-1" htmlFor="subcategory">
                      Subcategory
                    </label>
                    <div className="md:col-span-3">
                      {!showCustomSubcategory ? (
                        <select
                          className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 disabled:bg-gray-100/80 dark:disabled:bg-slate-800/60 disabled:cursor-not-allowed"
                          id="subcategory"
                          value={formData.subcategory_id}
                          onChange={e => handleSubcategoryChange(e.target.value)}
                          disabled={!canUpdate || !isEditing || !formData.category_id}
                          title={isCustomer ? "Subcategory cannot be changed after ticket creation" : (canUpdate && isEditing ? "Select a subcategory or add a custom one" : "")}
                        >
                          <option value="">-- None --</option>
                          {subcategories.map(sub => (
                            <option key={sub.id} value={sub.id}>{sub.name}</option>
                          ))}
                          {canUpdate && isEditing && (
                            <option value="custom" className="font-semibold text-indigo-600 dark:text-indigo-400">+ Add Custom Subcategory</option>
                          )}
                        </select>
                      ) : (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            className="flex-1 rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                            placeholder="Enter custom subcategory name"
                            value={customSubcategoryName}
                            onChange={e => handleCustomSubcategoryChange(e.target.value)}
                            autoFocus
                            disabled={!canUpdate || !isEditing}
                          />
                          {canUpdate && isEditing && (
                            <button
                              type="button"
                              onClick={() => {
                                setShowCustomSubcategory(false);
                                setCustomSubcategoryName('');
                                setFormData(prev => ({ ...prev, subcategory_id: '' }));
                              }}
                              className="px-4 py-2 text-sm border border-gray-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-all duration-200"
                            >
                              Cancel
                            </button>
                          )}
                      </div>
                      )}
                      {!isEditing && ticket?.subcategory_name && !ticket?.subcategory_id && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                          Custom: {ticket.subcategory_name}
                        </p>
                      )}
                    </div>
                </div>

                  {!isCustomer && (
                    <>
                      <div className="flex flex-col gap-1.5 md:grid md:grid-cols-4 md:items-center">
                        <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 md:col-span-1" htmlFor="impact">
                          Impact
                        </label>
                        <div className="md:col-span-3">
                        <select
                            className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 disabled:bg-gray-100/80 dark:disabled:bg-slate-800/60 disabled:cursor-not-allowed"
                            id="impact"
                            value={formData.impact}
                            onChange={e => {
                              const newImpact = e.target.value;
                              const newPriority = Math.round((Number(newImpact) + Number(formData.urgency)) / 2);
                              setFormData({ ...formData, impact: newImpact, priority: newPriority.toString() });
                            }}
                            disabled={!canUpdate || !isEditing}
                            title="How many users/systems are affected"
                          >
                            <option value="1">1 - Critical</option>
                            <option value="2">2 - High</option>
                            <option value="3">3 - Medium</option>
                            <option value="4">4 - Low</option>
                        </select>
                      </div>
                      </div>

                      <div className="flex flex-col gap-1.5 md:grid md:grid-cols-4 md:items-center">
                        <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 md:col-span-1" htmlFor="urgency">
                          Urgency
                        </label>
                        <div className="md:col-span-3">
                          <select
                            className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 disabled:bg-gray-100/80 dark:disabled:bg-slate-800/60 disabled:cursor-not-allowed"
                            id="urgency"
                            value={formData.urgency}
                            onChange={e => {
                              const newUrgency = e.target.value;
                              const newPriority = Math.round((Number(formData.impact) + Number(newUrgency)) / 2);
                              setFormData({ ...formData, urgency: newUrgency, priority: newPriority.toString() });
                            }}
                            disabled={!canUpdate || !isEditing}
                            title="How quickly this needs to be resolved"
                          >
                            <option value="1">1 - Critical</option>
                            <option value="2">2 - High</option>
                            <option value="3">3 - Medium</option>
                            <option value="4">4 - Low</option>
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex flex-col gap-1.5 md:grid md:grid-cols-4 md:items-center">
                    <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 md:col-span-1" htmlFor="priority">
                      Priority
                    </label>
                    <div className="md:col-span-3">
                      <select
                        className="w-full border border-dashed border-gray-300 dark:border-slate-600 rounded-xl bg-gray-100/80 dark:bg-slate-800/60 text-gray-500 dark:text-gray-400 px-4 py-2.5 text-sm cursor-not-allowed"
                        id="priority"
                        value={formData.priority}
                        disabled
                        title={isCustomer ? "Ticket priority level" : "Auto-calculated from Impact and Urgency"}
                      >
                        <option value={formData.priority}>{formData.priority} - {getPriorityLabel(formData.priority)}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex flex-col gap-1.5 md:grid md:grid-cols-4 md:items-center">
                    <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 md:col-span-1" htmlFor="opened">
                      Opened
                    </label>
                    <div className="md:col-span-3 relative">
                      <input
                        className="w-full border border-dashed border-gray-300 dark:border-slate-600 rounded-xl bg-gray-100/80 dark:bg-slate-800/60 text-gray-500 dark:text-gray-400 pr-8 px-4 py-2.5 text-sm font-mono cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500/60"
                        id="opened"
                        type="text"
                        value={openedDisplay}
                        readOnly
                        />
                      </div>
                  </div>

                  <div className="flex flex-col gap-1.5 md:grid md:grid-cols-4 md:items-center">
                    <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 md:col-span-1" htmlFor="opened-by">
                      Opened by
                    </label>
                    <div className="md:col-span-3 relative">
                      <input
                        className="w-full border border-dashed border-gray-300 dark:border-slate-600 rounded-xl bg-gray-100/80 dark:bg-slate-800/60 text-gray-500 dark:text-gray-400 pr-12 px-4 py-2.5 text-sm cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500/60"
                        id="opened-by"
                        type="text"
                        value={cleanUserName(ticket.opened_by_name || user?.name || 'System Administrator')}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5 md:grid md:grid-cols-4 md:items-center">
                    <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 md:col-span-1" htmlFor="contact-type">
                      Contact type
                    </label>
                    <div className="md:col-span-3">
                      <select
                        className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 disabled:bg-gray-100/80 dark:disabled:bg-slate-800/60 disabled:cursor-not-allowed"
                        id="contact-type"
                        value={formData.contact_type}
                        onChange={e => setFormData({ ...formData, contact_type: e.target.value })}
                        disabled={!isEditing || (isCustomer && !canCustomerEdit)}
                      >
                        <option value="phone">Phone</option>
                        <option value="email">Email</option>
                        <option value="self_service">Self-service</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5 md:grid md:grid-cols-4 md:items-center">
                    <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 md:col-span-1" htmlFor="state">
                      State
                    </label>
                    <div className="md:col-span-3">
                      <select
                        className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 disabled:bg-gray-100/80 dark:disabled:bg-slate-800/60 disabled:cursor-not-allowed"
                        id="state"
                        value={formData.status}
                        onChange={e => setFormData({ ...formData, status: e.target.value })}
                        disabled={!canUpdate || !isEditing}
                        title={isCustomer ? "Status is managed by support staff" : ""}
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">Active</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                      </div>
                  </div>

                  {!isCustomer && (
                    <div className="flex flex-col gap-1.5 md:grid md:grid-cols-4 md:items-center">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 md:col-span-1" htmlFor="assignment-group">
                        Assignment group
                      </label>
                      <div className="md:col-span-3">
                        <ReferenceField
                          value={formData.assignment_group_id?.toString() || ''}
                          onChange={val => setFormData({ ...formData, assignment_group_id: val })}
                          items={assignmentGroups}
                          placeholder="Search by name, description, or email..."
                          displayKey="name"
                          disabled={!canUpdate || !isEditing}
                          showLock={false}
                          showInfo={false}
                          showSearch={true}
                          infoTooltip="Select the team that should handle this ticket. Search by name, description, or email address."
                          fieldName="Assignment group"
                          showDetails={true}
                          searchFields={['description', 'email']}
                          onSearchClick={() => openLookupModal('assignment_group', assignmentGroups, 'Select Assignment Group', [
                            { key: 'name', label: 'Name', width: '40%' },
                            { key: 'description', label: 'Description', width: '40%' },
                            { key: 'email', label: 'Email', width: '20%' }
                          ], 'name')}
                        />
                    </div>
                  </div>
                )}

                  <div className="flex flex-col gap-1.5 md:grid md:grid-cols-4 md:items-center">
                    <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 md:col-span-1" htmlFor="assigned-to">
                      Assigned to
                    </label>
                    <div className="md:col-span-3">
                      {!isCustomer && (
                        <ReferenceField
                          value={formData.assigned_to?.toString() || ''}
                          onChange={val => setFormData({ ...formData, assigned_to: val })}
                          items={employees}
                          placeholder="Search..."
                          disabled={!canUpdate || !isEditing}
                          showLock={false}
                          showInfo={false}
                          showSearch={true}
                          infoTooltip="Assign this incident to a specific user"
                          fieldName="Assigned to"
                          onSearchClick={() => openLookupModal('assigned_to', employees, 'Select Assigned To', [
                            { key: 'name', label: 'Name', width: '30%' },
                            { key: 'email', label: 'Email', width: '30%' },
                            { key: 'role', label: 'Role', width: '20%' },
                            { key: 'department', label: 'Department', width: '20%' }
                          ], 'name')}
                        />
                      )}
                      {isCustomer && !isEditing && ticket.assigned_to_name && (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            className="flex-1 border border-dashed border-gray-300 dark:border-slate-600 rounded-xl bg-gray-100/80 dark:bg-slate-800/60 text-gray-500 dark:text-gray-400 px-4 py-2.5 text-sm cursor-not-allowed transition-all duration-200"
                            value={`Assigned to: ${cleanUserName(ticket.assigned_to_name || '')}`}
                            readOnly
                          />
                          <Tooltip content="View employee contact details">
                            <button
                              type="button"
                              onClick={() => {
                                const assignedEmployee = employees.find(e => 
                                  e.id.toString() === ticket.assigned_agent_id?.toString() ||
                                  e.id.toString() === formData.assigned_to?.toString()
                                );
                                
                                const employeeData = assignedEmployee || {
                                  id: ticket.assigned_agent_id || formData.assigned_to,
                                  name: cleanUserName(ticket.assigned_to_name || ''),
                                  email: ticket.assigned_to_email || assignedEmployee?.email || 'N/A',
                                  role: assignedEmployee?.role || 'Employee',
                                  department: assignedEmployee?.department || 'N/A',
                                  phone: assignedEmployee?.phone || assignedEmployee?.mobile_phone || 'N/A'
                                };
                                
                                setLookupModal({
                                  isOpen: true,
                                  field: 'assigned_to',
                                  data: [employeeData],
                                  title: 'Assigned Employee Details',
                                  columns: [
                                    { key: 'name', label: 'Name', width: '25%' },
                                    { key: 'email', label: 'Email', width: '30%' },
                                    { key: 'phone', label: 'Phone', width: '20%' },
                                    { key: 'role', label: 'Role', width: '15%' },
                                    { key: 'department', label: 'Department', width: '10%' }
                                  ],
                                  displayKey: 'name'
                                });
                              }}
                              className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200"
                            >
                              <Info className="w-4 h-4" />
                            </button>
                          </Tooltip>
                            </div>
                                )}
                              </div>
                            </div>
                          </div>

                <div className="md:col-span-2">
                  <div className="grid grid-cols-1 md:grid-cols-8 items-start">
                    <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 pt-2 col-span-1" htmlFor="short-description">
                      Short description
                    </label>
                    <div className="col-span-1 md:col-span-7 relative">
                      <input
                        className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 pr-8 disabled:bg-gray-100/80 dark:disabled:bg-slate-800/60 disabled:cursor-not-allowed"
                        id="short-description"
                        type="text"
                        value={formData.subject}
                        onChange={e => setFormData({ ...formData, subject: e.target.value })}
                        maxLength={80}
                        placeholder="Enter a brief description of the issue"
                        disabled={!isEditing || (isCustomer && !canCustomerEdit)}
                      />
                        </div>
                  </div>
                  </div>

                <div className="md:col-span-2">
                  <div className="grid grid-cols-1 md:grid-cols-8 items-start">
                    <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 pt-2 col-span-1" htmlFor="description">
                      Description
                    </label>
                    <div className="col-span-1 md:col-span-7">
                      <textarea
                        className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none disabled:bg-gray-100/80 dark:disabled:bg-slate-800/60 disabled:cursor-not-allowed"
                        id="description"
                        rows={4}
                        value={formData.description}
                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Enter detailed description of the issue, steps to reproduce, error messages, etc."
                        disabled={!isEditing || (isCustomer && !canCustomerEdit)}
                      />
                    </div>
                  </div>
                </div>

                {ticket.resolution && (
                  <div className="md:col-span-2">
                    <div className="grid grid-cols-1 md:grid-cols-8 items-start">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 pt-2 col-span-1" htmlFor="resolution">
                        Resolution
                      </label>
                      <div className="col-span-1 md:col-span-7">
                        <textarea
                          className="w-full rounded-xl px-4 py-2.5 text-sm bg-emerald-50/50 dark:bg-emerald-900/20 border border-emerald-300 dark:border-emerald-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-emerald-400 dark:hover:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none disabled:bg-emerald-50/30 dark:disabled:bg-emerald-900/10"
                          id="resolution"
                          rows={2}
                          value={formData.resolution || ''}
                          onChange={e => setFormData({ ...formData, resolution: e.target.value })}
                          placeholder="Enter resolution details..."
                          disabled={!canUpdate || !isEditing}
                          title={isCustomer ? "Resolution is provided by support staff" : ""}
                        />
                      </div>
                        </div>
                        </div>
                      )}
                  </form>
              </div>

            <div className="relative bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 dark:border-slate-700/60 overflow-hidden shadow-lg shadow-indigo-500/5">
              <div className="px-4 py-3 border-b border-dashed border-gray-300/60 dark:border-slate-700/60 bg-gradient-to-r from-gray-50 to-indigo-50/40 dark:from-slate-900 dark:to-indigo-950/30">
                <div className="flex justify-between items-center">
                    <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Notes & Activity</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Add comments and track changes</p>
                      </div>
                  <button
                    type="button"
                    className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200"
                    onClick={() => setNotesExpanded(!notesExpanded)}
                  >
                    {notesExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                    </div>
                        </div>

              {notesExpanded && (
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mb-4">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-32 flex-shrink-0">Watch list</span>
                        <div className="flex-grow flex items-center space-x-2">
                          <Tooltip content={watchListLocked ? 'Unlock watch list' : 'Lock watch list'}>
                            <button
                              type="button"
                              className={`p-2.5 rounded-xl border transition-all duration-200 ${
                                watchListLocked
                                  ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                                  : 'border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 text-gray-500 dark:text-gray-400'
                              }`}
                              onClick={() => {
                                setWatchListLocked(!watchListLocked);
                                toast.info(watchListLocked ? 'Watch list unlocked' : 'Watch list locked');
                              }}
                            >
                              <Lock className="w-5 h-5" />
                            </button>
                          </Tooltip>
                          {canManageWatchList && (
                            <Tooltip content="Add user to Watch List">
                              <button
                                type="button"
                                className="p-2.5 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 text-gray-500 dark:text-gray-400 transition-all duration-200"
                                onClick={() => {
                                  setWatchListModalSearch('');
                                  setShowAddWatchListModal(true);
                                }}
                              >
                                <UserPlus className="w-5 h-5" />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      {/* Watch List members */}
                      <div className="ml-0 md:ml-32">
                        {watchListLoading ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400">Loading...</p>
                        ) : watchList.length === 0 ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400">No users on Watch List. Add users to notify them on ticket updates.</p>
                        ) : (
                          <ul className="space-y-2 max-h-44 overflow-y-auto overscroll-contain pr-1 [scrollbar-width:thin] [scrollbar-color:rgb(203_213_225)_transparent] dark:[scrollbar-color:rgb(71_85_105)_transparent] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300/90 dark:[&::-webkit-scrollbar-thumb]:bg-slate-600">
                            {watchList.map((member) => (
                              <li key={member.id} className="flex items-center justify-between gap-2 py-1.5 px-3 rounded-lg bg-gray-50 dark:bg-slate-800/60 border border-gray-100 dark:border-slate-700">
                                <div className="flex items-center gap-2 min-w-0">
                                  <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{member.name || 'Unknown'}</span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate hidden sm:inline">({member.email})</span>
                                </div>
                                {canManageWatchList && (canUpdate || member.id === user?.id) && (
                                  <Tooltip content="Remove from Watch List">
                                    <button
                                      type="button"
                                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                      onClick={() => handleRemoveFromWatchList(member.id)}
                                    >
                                      <UserMinus className="w-4 h-4" />
                                    </button>
                                  </Tooltip>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    {/* Work notes list - Only visible to admins/employees */}
                    {!isCustomer && (
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-32 flex-shrink-0">Work notes list</span>
                          <div className="flex-grow flex items-center gap-2">
                            <Tooltip content={workNotesLocked ? 'Unlock work notes' : 'Lock work notes'}>
                              <button
                                type="button"
                                className={`p-2.5 rounded-xl border transition-all duration-200 ${
                                  workNotesLocked
                                    ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                                    : 'border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 text-gray-500 dark:text-gray-400'
                                }`}
                                onClick={() => {
                                  setWorkNotesLocked(!workNotesLocked);
                                  toast.info(workNotesLocked ? 'Work notes unlocked' : 'Work notes locked');
                                }}
                              >
                                <Lock className="w-5 h-5" />
                              </button>
                            </Tooltip>
                            {canManageWorkNotesList && (
                              <Tooltip content="Add staff to Work notes list (notified on internal work notes)">
                                <button
                                  type="button"
                                  className="p-2.5 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 text-gray-500 dark:text-gray-400 transition-all duration-200"
                                  onClick={() => setShowAddWorkNotesListModal(true)}
                                >
                                  <UserPlus className="w-5 h-5" />
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                        <div className="ml-0 md:ml-32">
                          {workNotesListLoading ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400">Loading...</p>
                          ) : workNotesList.length === 0 ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400">No users on Work notes list. Add staff to notify them when internal work notes are added.</p>
                          ) : (
                            <ul className="space-y-2">
                              {workNotesList.map((member) => (
                                <li key={member.id} className="flex items-center justify-between gap-2 py-1.5 px-3 rounded-lg bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/40">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <User className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{member.name || 'Unknown'}</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate hidden sm:inline">({member.email})</span>
                                  </div>
                                  {canManageWorkNotesList && (
                                    <Tooltip content="Remove from Work notes list">
                                      <button
                                        type="button"
                                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                        onClick={() => handleRemoveFromWorkNotesList(member.id)}
                                      >
                                        <UserMinus className="w-4 h-4" />
                                      </button>
                                    </Tooltip>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}
                      </div>

                  <div className="mt-4 space-y-4">
                      <div>
                      <label className="block text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 mb-1">
                        Attachments
                      </label>
                      <div className="border border-gray-200/60 dark:border-slate-700/60 rounded-xl bg-gray-50/60 dark:bg-slate-900/40 p-4 space-y-3">
                        <form className="flex flex-col sm:flex-row items-start sm:items-center gap-2" onSubmit={handleUploadAttachments}>
                          <input
                            type="file"
                            multiple
                            onChange={handleAttachmentFileChange}
                            className="block w-full text-xs text-slate-600 dark:text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 dark:file:bg-indigo-900/40 dark:file:text-indigo-200 cursor-pointer"
                          />
                          <Button
                            variant="unstyled"
                            type="submit"
                            disabled={submitting || uploadFiles.length === 0}
                            className="inline-flex items-center justify-center gap-2 h-9 px-4 text-sm font-semibold rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-md shadow-indigo-500/40 hover:shadow-xl hover:shadow-indigo-500/50 hover:-translate-y-0.5 hover:scale-[1.03] active:scale-95 transition-all duration-300 ease-out disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:-translate-y-0 whitespace-nowrap"
                          >
                            Upload
                          </Button>
                        </form>
                        <div className="max-h-56 overflow-y-auto mt-2 space-y-2">
                          {attachmentsLoading ? (
                            <p className="text-xs text-slate-500">Loading attachments...</p>
                          ) : attachments && attachments.length > 0 ? (
                            attachments.map(att => (
                              <div
                                key={att.id}
                                className="flex items-center justify-between text-xs px-3 py-2 rounded-xl bg-white dark:bg-slate-800 border border-gray-200/60 dark:border-slate-700/60 shadow-sm"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-slate-700 dark:text-slate-200 truncate">{att.file_name}</p>
                                    <p className="text-[10px] text-slate-400">
                                      {att.file_size ? `${(att.file_size / 1024).toFixed(1)} KB` : ''}
                      </p>
                    </div>
                        </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadAttachment(att)}
                                    className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200"
                                    title="Download"
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                  {(isStaffPortal || att.user_id === user?.id) && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteAttachment(att.id)}
                                      className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-300 transition-all duration-200"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                      </div>
                      </div>
                            ))
                          ) : (
                            <p className="text-xs text-slate-500">No attachments yet</p>
                    )}
                  </div>
                </div>
                    </div>
                      <div>
                      <label className="block text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 mb-1" htmlFor="additional-comments">
                        Additional comments <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(Customer visible)</span>
                      </label>
                      <textarea
                        className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none"
                        id="additional-comments"
                        rows={2}
                        value={additionalComments}
                        onChange={e => setAdditionalComments(e.target.value)}
                        placeholder="Comments visible to the customer"
                      />
                      <Button
                        variant="unstyled"
                        onClick={handleAddAdditionalComment}
                        disabled={submitting || !additionalComments.trim()}
                        className="mt-2 inline-flex items-center justify-center gap-2 h-10 px-4 text-sm font-semibold rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-md shadow-indigo-500/40 hover:shadow-xl hover:shadow-indigo-500/50 hover:-translate-y-0.5 hover:scale-[1.03] active:scale-95 transition-all duration-300 ease-out disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:-translate-y-0 whitespace-nowrap"
                      >
                        Add Comment
                      </Button>
                        </div>
                    {/* Work notes section - Only visible to admins/employees */}
                    {!isCustomer && (
                      <div>
                        <label className="block text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 mb-1" htmlFor="work-notes">
                          Work notes <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(Internal only)</span>
                        </label>
                        <textarea
                          className="w-full rounded-xl px-4 py-2.5 text-sm bg-amber-50/50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-amber-400 dark:hover:border-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none disabled:bg-amber-50/30 dark:disabled:bg-amber-900/10"
                          id="work-notes"
                          rows={2}
                          value={workNotes}
                          onChange={e => setWorkNotes(e.target.value)}
                          placeholder="Internal work notes (not visible to customer)"
                          disabled={!canUpdate}
                        />
                        {canUpdate && (
                          <Button
                            variant="unstyled"
                            onClick={handleAddWorkNote}
                            disabled={submitting || !workNotes.trim()}
                            className="mt-2 inline-flex items-center justify-center gap-2 h-10 px-4 text-sm font-semibold rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-md shadow-indigo-500/40 hover:shadow-xl hover:shadow-indigo-500/50 hover:-translate-y-0.5 hover:scale-[1.03] active:scale-95 transition-all duration-300 ease-out disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:-translate-y-0 whitespace-nowrap"
                          >
                            Add Work Note
                          </Button>
                        )}
                      </div>
                    )}
                </div>

                  <div className="mt-6 pt-6 border-t border-dashed border-gray-300/60 dark:border-slate-700/60">
                    <div className="flex items-start gap-4">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 w-28 flex-shrink-0 pt-2" htmlFor="activity">
                        Activity
                      </label>
                      <div className="flex-1 min-w-0">
                        {/* Header: count + icons (same as CreateTicket) */}
                        <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {(() => {
                              const commentsCount = ticket.comments ? ticket.comments.filter(c => !isCustomer || !c.is_internal).length : 0;
                              const historyCount = ticket.history ? ticket.history.length : 0;
                              const total = activityFilter === 'all' ? commentsCount + historyCount : activityFilter === 'comments' ? commentsCount : historyCount;
                              return `${total} ${total === 1 ? 'item' : 'items'}${activityFilter !== 'all' ? ` (${activityFilter === 'comments' ? 'comments only' : 'history only'})` : ''}`;
                            })()}
                          </span>
                          <div className="flex items-center gap-1" ref={activityFilterRef}>
                            <Tooltip content="Filter activity: All, Comments only, or History only">
                              <div className="relative">
                                <button
                                  type="button"
                                  onClick={() => setActivityFilterOpen(!activityFilterOpen)}
                                  className={`p-2 rounded-xl border transition-all duration-200 ${activityFilterOpen ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-700' : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400'}`}
                                >
                                  <Filter className="w-4 h-4" />
                                </button>
                                {activityFilterOpen && (
                                  <div className="absolute right-0 top-full mt-1 py-1 min-w-[160px] bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-lg z-20">
                                    <button type="button" onClick={() => { setActivityFilter('all'); setActivityFilterOpen(false); }} className={`w-full text-left px-3 py-2 text-sm ${activityFilter === 'all' ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>All activity</button>
                                    <button type="button" onClick={() => { setActivityFilter('comments'); setActivityFilterOpen(false); }} className={`w-full text-left px-3 py-2 text-sm ${activityFilter === 'comments' ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>Comments only</button>
                                    <button type="button" onClick={() => { setActivityFilter('history'); setActivityFilterOpen(false); }} className={`w-full text-left px-3 py-2 text-sm ${activityFilter === 'history' ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>History only</button>
                                  </div>
                                )}
                              </div>
                            </Tooltip>
                            <Tooltip content={activityExpanded ? 'Collapse activity' : 'Expand activity'}>
                              <button
                                type="button"
                                onClick={() => setActivityExpanded(!activityExpanded)}
                                className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200"
                              >
                                {activityExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                        {activityExpanded && (
                        <>
                        {(activityFilter === 'all' || activityFilter === 'comments') && ticket.comments && ticket.comments.length > 0 ? ticket.comments
                          // Frontend safety filter: Customers should never see internal comments
                          .filter(comment => {
                            if (isCustomer && comment.is_internal) {
                              return false; // Hide internal comments from customers
                            }
                            return true;
                          })
                          .map((comment, idx) => (
                          <div key={`comment-${comment.id}`} className="mb-3">
                            <div className="flex justify-between items-center mb-1.5">
                              <div className="text-xs text-slate-500 dark:text-slate-400">
                                <span className="font-medium text-slate-600 dark:text-slate-300">
                                  {new Date(comment.created_at).toLocaleString()}
                              </span>
                                {' - '}
                                <span className="font-semibold text-slate-700 dark:text-slate-200">
                                  {(() => {
                                    if (!comment.user_name) return 'System';
                                    let cleaned = String(comment.user_name).trim();
                                    cleaned = cleaned.replace(/0+$/, '');
                                    cleaned = cleaned.replace(/0+$/, '');
                                    cleaned = cleanUserName(cleaned);
                                    cleaned = cleaned.replace(/0+$/, '');
                                    while (cleaned.endsWith('0')) {
                                      cleaned = cleaned.substring(0, cleaned.length - 1).trim();
                                    }
                                    if (process.env.NODE_ENV === 'development' && cleaned.includes('0') && cleaned.endsWith('0')) {
                                      console.error('[FRONTEND] Still has trailing zero:', cleaned, 'Original:', comment.user_name);
                                    }
                                    return cleaned || 'System';
                                  })()}
                                </span>
                                {/* Only show "Internal" badge to admins/employees */}
                                {comment.is_internal && isStaffPortal ? (
                                  <span className="text-xs ml-2 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
                                    Internal
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <div className="bg-slate-50 dark:bg-slate-900/30 p-3 rounded-lg border border-slate-200 dark:border-slate-700/50">
                              <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">{comment.comment}</p>
                            </div>
                          </div>
                        )) : null}
                        {(activityFilter === 'all' || activityFilter === 'history') && ticket.history && ticket.history.length > 0 ? ticket.history.map((item, idx) => (
                          <div key={`history-${item.id}`} className="mb-3">
                            <div className="flex justify-between items-center mb-1.5">
                              <div className="text-xs text-slate-500 dark:text-slate-400">
                                <span className="font-medium text-slate-600 dark:text-slate-300">
                                {new Date(item.created_at).toLocaleString()}
                              </span>
                                {' - '}
                                <span className="font-semibold text-slate-700 dark:text-slate-200">
                                  {(() => {
                                    if (!item.user_name) return 'System';
                                    let cleaned = String(item.user_name).trim();
                                    cleaned = cleaned.replace(/0+$/, '');
                                    cleaned = cleaned.replace(/0+$/, '');
                                    cleaned = cleanUserName(cleaned);
                                    cleaned = cleaned.replace(/0+$/, '');
                                    while (cleaned.endsWith('0')) {
                                      cleaned = cleaned.substring(0, cleaned.length - 1).trim();
                                    }
                                    if (process.env.NODE_ENV === 'development' && cleaned.includes('0') && cleaned.endsWith('0')) {
                                      console.error('[FRONTEND] Still has trailing zero:', cleaned, 'Original:', item.user_name);
                                    }
                                    return cleaned || 'System';
                                  })()}
                                </span>
                                <span className="text-slate-400 dark:text-slate-500 text-xs ml-2">
                                  {formatHistoryItem(item)}
                              </span>
                            </div>
                          </div>
                        </div>
                        )) : null}
                        {(() => {
                          const commentsCount = ticket.comments ? ticket.comments.filter(c => !isCustomer || !c.is_internal).length : 0;
                          const historyCount = ticket.history ? ticket.history.length : 0;
                          const showComments = (activityFilter === 'all' || activityFilter === 'comments') && commentsCount > 0;
                          const showHistory = (activityFilter === 'all' || activityFilter === 'history') && historyCount > 0;
                          if (!showComments && !showHistory) {
                            return <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No activity yet.{activityFilter !== 'all' ? ` No ${activityFilter === 'comments' ? 'comments' : 'history'} to show.` : ''}</p>;
                          }
                          return null;
                        })()}
                        </>
                        )}
                      </div>
                    </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
        </div>
      </div>

      </DashboardLayout>

      {/* Add to Watch List modal */}
      {showAddWatchListModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => {
            setShowAddWatchListModal(false);
            setWatchListModalSearch('');
          }}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-xl md:rounded-2xl shadow-xl w-full max-w-sm md:max-w-md flex flex-col max-h-[85vh] overflow-hidden border border-gray-200 dark:border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700 shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add to Watch List</h3>
              <button
                type="button"
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
                onClick={() => {
                  setShowAddWatchListModal(false);
                  setWatchListModalSearch('');
                }}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="px-4 pt-3 pb-2 border-b border-gray-200 dark:border-slate-700 shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                <input
                  type="search"
                  autoComplete="off"
                  placeholder="Search by name or email..."
                  value={watchListModalSearch}
                  onChange={(e) => setWatchListModalSearch(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                />
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4 overscroll-contain [scrollbar-width:thin] [scrollbar-color:rgb(203_213_225)_transparent] dark:[scrollbar-color:rgb(71_85_105)_transparent] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300/90 dark:[&::-webkit-scrollbar-thumb]:bg-slate-600">
              {(() => {
                const watchListIds = new Set((watchList || []).map((w) => Number(w.id)));
                let available = (watchListPickerUsers || []).filter((u) => !watchListIds.has(Number(u.id)));
                const q = watchListModalSearch.trim().toLowerCase();
                if (q) {
                  available = available.filter((u) => {
                    const name = (cleanUserName(u.name || '') || '').toLowerCase();
                    const email = (u.email || '').toLowerCase();
                    return name.includes(q) || email.includes(q);
                  });
                }
                if (available.length === 0) {
                  return (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {q ? 'No users match your search.' : 'All users are already on the Watch List.'}
                    </p>
                  );
                }
                return (
                  <ul className="space-y-1">
                    {available.map((u) => (
                      <li key={u.id}>
                        <button
                          type="button"
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border border-transparent hover:border-indigo-200 dark:hover:border-indigo-800 transition-colors"
                          onClick={() => handleAddToWatchList(u.id)}
                        >
                          <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 block truncate">{cleanUserName(u.name || '') || u.email || 'User'}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 truncate block">{u.email}</span>
                          </div>
                          <UserPlus className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Add to Work notes list modal - staff only */}
      {showAddWorkNotesListModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowAddWorkNotesListModal(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl md:rounded-2xl shadow-xl w-full max-w-sm md:max-w-md max-h-[85vh] overflow-hidden border border-amber-200 dark:border-amber-800" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/20">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add to Work notes list</h3>
              <button type="button" className="p-2 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40" onClick={() => setShowAddWorkNotesListModal(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Only staff (admin/employee) receive notifications when internal work notes are added.</p>
              {(() => {
                const staffUsers = (users || []).filter((u) => u.role === 'admin' || u.role === 'employee');
                const workNotesListIds = new Set((workNotesList || []).map((w) => Number(w.id)));
                const available = staffUsers.filter((u) => !workNotesListIds.has(Number(u.id)));
                if (available.length === 0) {
                  return <p className="text-sm text-gray-500 dark:text-gray-400">All staff are already on the Work notes list.</p>;
                }
                return (
                  <ul className="space-y-1">
                    {available.map((u) => (
                      <li key={u.id}>
                        <button
                          type="button"
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-amber-50 dark:hover:bg-amber-900/20 border border-transparent hover:border-amber-200 dark:hover:border-amber-800 transition-colors"
                          onClick={() => handleAddToWorkNotesList(u.id)}
                        >
                          <User className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 block truncate">{cleanUserName(u.name || '') || u.email || 'User'}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 truncate block">{u.email}</span>
                          </div>
                          <UserPlus className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <ReferenceLookupModal
        isOpen={lookupModal.isOpen}
        onClose={closeLookupModal}
        onSelect={handleLookupSelect}
        title={lookupModal.title}
        data={lookupModal.data}
        columns={lookupModal.columns}
        displayKey={lookupModal.displayKey}
      />
    </>
  );
};

export default TicketDetail;